import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import './DashboardSchoolBranches.css';

const DashboardSchoolBranches = ({ schoolData, dataLoading, onRefresh }) => {
    const navigate = useNavigate();
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        school_id: '', // Unique Login ID for the branch
        password: '',
        max_students: 0,
        max_teachers: 0,
        location: ''
    });

    const [idStatus, setIdStatus] = useState('idle'); // 'idle', 'checking', 'available', 'taken', 'error'
    const [editingBranch, setEditingBranch] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isCreatingBranch, setIsCreatingBranch] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [createdBranchData, setCreatedBranchData] = useState(null);

    // Fetch branches
    useEffect(() => {
        if (schoolData?.id) {
            fetchBranches();
        }
    }, [schoolData]);

    const fetchBranches = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('schools')
                .select('*')
                .eq('parent_school_id', schoolData.id)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setBranches(data || []);
        } catch (err) {
            } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'school_id' && !editingBranch) {
            // Reset status on change (only if not editing)
            setIdStatus('idle');
        }
    };

    const handleManage = (branch) => {
        setEditingBranch(branch);
        setFormData({
            name: branch.name,
            school_id: branch.school_id,
            password: branch.password || '',
            max_students: branch.max_students || 0,
            max_teachers: branch.max_teachers || 0,
            location: branch.branch_name || ''
        });
        setIdStatus('available'); // Assume valid for existing
        setShowAddModal(true);
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
        setEditingBranch(null);
        setFormData({
            name: '',
            school_id: '',
            password: '',
            max_students: 0,
            max_teachers: 0,
            location: ''
        });
        setIdStatus('idle');
        setError('');
        setSuccess('');
    };

    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false);
        setCreatedBranchData(null);
        handleCloseModal();
    };

    const checkSchoolId = async (id) => {
        if (!id || id.length < 3) {
            setIdStatus('error'); // Too short
            return;
        }

        setIdStatus('checking');
        try {
            // Check in schools table (and ideally users table if ids are shared, but schools table has unique constraint on school_id)
            const { data, error } = await supabase
                .from('schools')
                .select('id')
                .eq('school_id', id)
                .maybeSingle();

            if (error) throw error;

            if (data) {
                setIdStatus('taken');
            } else {
                setIdStatus('available');
            }
        } catch (err) {setIdStatus('error');
        }
    };

    const handleIdBlur = () => {
        if (formData.school_id) {
            checkSchoolId(formData.school_id);
        }
    };

    const handleBranchClick = (branch) => {
        // IMPORTANT: Do NOT write branch context into localStorage.
        // localStorage is shared across tabs and will "flip" the main dashboard into the branch context.
        // Instead, pass the branch school UUID via query param and let the new tab store it in sessionStorage.
        const url = `/mydashboard?schoolId=${encodeURIComponent(branch.id)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleCreateBranch = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Prevent submission if ID is taken or not checked
        if (idStatus === 'taken') {
            setError('Please choose a different Login ID. This one is already taken.');
            return;
        }
        if (idStatus === 'checking') {
            setError('Please wait for ID validation.');
            return;
        }

        // Validation: Check if we have enough quota
        // For Edit: Calculate difference
        const studentDiff = parseInt(formData.max_students) - (editingBranch ? (editingBranch.max_students || 0) : 0);
        const teacherDiff = parseInt(formData.max_teachers) - (editingBranch ? (editingBranch.max_teachers || 0) : 0);

        if (studentDiff > schoolData.max_students) {
            setError(`Insufficient Student Quota. You only have ${schoolData.max_students} available.`);
            return;
        }
        if (teacherDiff > schoolData.max_teachers) {
            setError(`Insufficient Teacher Quota. You only have ${schoolData.max_teachers} available.`);
            return;
        }

        // Set loading state for new branch creation
        if (!editingBranch) {
            setIsCreatingBranch(true);
        }

        try {
            if (editingBranch) {
                // UPDATE EXISTING BRANCH

                // Step A: Deduct/Refund Main Pool
                // Note: If diff is positive, we deduct. If negative, we add back.
                const { error: updateParentError } = await supabase
                    .from('schools')
                    .update({
                        max_students: schoolData.max_students - studentDiff,
                        max_teachers: schoolData.max_teachers - teacherDiff
                    })
                    .eq('id', schoolData.id);

                if (updateParentError) throw updateParentError;

                // Step B: Update Branch
                const { error: updateBranchError } = await supabase
                    .from('schools')
                    .update({
                        name: formData.name,
                        branch_name: formData.location,
                        password: formData.password, // Update password if changed
                        max_students: parseInt(formData.max_students),
                        max_teachers: parseInt(formData.max_teachers),
                        updated_at: new Date()
                    })
                    .eq('id', editingBranch.id);

                if (updateBranchError) {
                    // Rollback Parent (Roughly)
                    await supabase.from('schools').update({
                        max_students: schoolData.max_students,
                        max_teachers: schoolData.max_teachers
                    }).eq('id', schoolData.id);
                    throw updateBranchError;
                }
                // For updates, just show success message and close
                setSuccess('Branch updated successfully!');
                handleCloseModal();
            } else {
                // CREATE NEW BRANCH

                // Step A: Deduct from Main
                const { error: updateError } = await supabase
                    .from('schools')
                    .update({
                        max_students: schoolData.max_students - parseInt(formData.max_students),
                        max_teachers: schoolData.max_teachers - parseInt(formData.max_teachers)
                    })
                    .eq('id', schoolData.id);

                if (updateError) throw updateError;

                // Step B: Create Branch (inherit parent's providers so branch has same providers as original school)
                const { data: newBranchData, error: insertError } = await supabase
                    .from('schools')
                    .insert([{
                        name: formData.name,
                        branch_name: formData.location, // Using 'location' as branch name or alias
                        school_id: formData.school_id,
                        password: formData.password,
                        parent_school_id: schoolData.id,
                        max_students: parseInt(formData.max_students),
                        max_teachers: parseInt(formData.max_teachers),
                        settings: schoolData.settings || {}, // Inherit settings
                        contact: { address: formData.location },
                        owner: schoolData.owner || {},
                        accessible_providers: schoolData.accessible_providers || [] // Branches always have same providers as parent
                    }])
                    .select();

                if (insertError) {
                    // Rollback deduction
                    await supabase.from('schools').update({
                        max_students: schoolData.max_students,
                        max_teachers: schoolData.max_teachers
                    }).eq('id', schoolData.id);

                    throw insertError;
                }

                // Step C: Inherit/Clone Grades & Subsections from Parent (HQ)
                const newBranchId = newBranchData[0].id; // Get the ID of the new branch

                // 1. Fetch Parent Grades
                const { data: parentGrades, error: gradesError } = await supabase
                    .from('grades')
                    .select('*')
                    .eq('school_id', schoolData.id);

                if (gradesError)// We don't throw here to avoid failing whole branch creation if cloning fails, but ideally we should.

                if (parentGrades && parentGrades.length > 0) {
                    // 2. Fetch Parent Sections
                    const parentGradeIds = parentGrades.map(g => g.id);
                    const { data: parentSections, error: sectionsError } = await supabase
                        .from('grade_sections')
                        .select('*')
                        .in('grade_id', parentGradeIds);

                    if (sectionsError)// 3. Clone Grades Loop
                    for (const pGrade of parentGrades) {
                        // Insert new Grade
                        const { data: newGradeData, error: newGradeError } = await supabase
                            .from('grades')
                            .insert([{
                                school_id: newBranchId,
                                name: pGrade.name,
                                order_index: pGrade.order_index,
                                is_active: pGrade.is_active
                            }])
                            .select();

                        if (newGradeError) {continue;
                        }

                        const newGradeId = newGradeData[0].id;

                        // 4. Clone Sections for this Grade
                        const relevantSections = (parentSections || []).filter(s => s.grade_id === pGrade.id);
                        if (relevantSections.length > 0) {
                            const sectionsToInsert = relevantSections.map(s => ({
                                grade_id: newGradeId,
                                name: s.name,
                                order_index: s.order_index,
                                is_active: s.is_active
                            }));

                            const { error: newSectionsError } = await supabase
                                .from('grade_sections')
                                .insert(sectionsToInsert);

                            if (newSectionsError) {}
                        }
                    }
                }

                // Store created branch data and show success modal
                setCreatedBranchData(newBranchData[0]);
                setShowSuccessModal(true);
            }

            // Refresh Data
            fetchBranches();
            if (onRefresh) onRefresh(); // Refresh parent to see updated quota

        } catch (err) {setError(err.message || 'Failed to save branch');
        } finally {
            setIsCreatingBranch(false);
        }
    };

    // Open Confirmation Dialog
    const handleDeleteBranchClick = (e) => {
        e.preventDefault(); // Prevent form submit
        setShowDeleteConfirm(true);
    };

    // Actual Delete execution
    const confirmDeleteBranch = async () => {
        if (!editingBranch) return;
        const branchId = editingBranch.id;

        try {
            setLoading(true);

            // Using RPC for atomic deletion and refund
            const { error: rpcError } = await supabase.rpc('delete_school_branch', {
                p_branch_id: branchId
            });

            if (rpcError) throw rpcError;

            // Success
            setSuccess('Branch deleted successfully');
            setShowDeleteConfirm(false);
            handleCloseModal(); // Close the manage modal too
            fetchBranches();
            if (onRefresh) onRefresh();

        } catch (err) {setError(`Error deleting branch: ${err.message}`);
            // If error, likely close confirm to show error in manage modal
            setShowDeleteConfirm(false);
        } finally {
            setLoading(false);
        }
    };

    if (loading && !branches.length) return <div className="branches-loading">Loading branches...</div>;

    return (
        <div className="branches-container">
            <div className="branches-header">
                <div className="branches-title">
                    <h3>My Branches</h3>
                    <p>Manage your school branches and distribute licenses.</p>
                </div>
                <button className="add-branch-btn" onClick={() => setShowAddModal(true)}>
                    + New Branch
                </button>
            </div>

            {/* Quota Overview Card */}
            <div className="quota-card">
                <div className="quota-item">
                    <span className="quota-label">Available Student Licenses (HQ Pool)</span>
                    <span className="quota-value">{schoolData.max_students}</span>
                </div>
                <div className="quota-divider"></div>
                <div className="quota-item">
                    <span className="quota-label">Available Teacher Licenses (HQ Pool)</span>
                    <span className="quota-value">{schoolData.max_teachers}</span>
                </div>
            </div>

            <div className="branches-list">
                {branches.length === 0 ? (
                    <div className="no-branches">
                        <p>No branches found. Create your first branch to expand.</p>
                    </div>
                ) : (
                    branches.map(branch => (
                        <div
                            key={branch.id}
                            className="branch-card"
                            onClick={() => handleBranchClick(branch)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="branch-info">
                                <h4>{branch.name}</h4>
                                <span className="branch-id">ID: {branch.school_id}</span>
                                {branch.branch_name && <span className="branch-location">📍 {branch.branch_name}</span>}
                            </div>

                            <div className="branch-stats">
                                <div className="stat">
                                    <span className="stat-label">Students</span>
                                    <span className="stat-val">{branch.max_students}</span>
                                </div>
                                <div className="stat">
                                    <span className="stat-label">Teachers</span>
                                    <span className="stat-val">{branch.max_teachers}</span>
                                </div>
                            </div>

                            <div className="branch-actions">
                                <button
                                    className="manage-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleManage(branch);
                                    }}
                                >
                                    Manage
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Add Modal */}
            {showAddModal && (
                <div className="modal-overlay">
                    <div className="modal-content branch-modal" style={{ position: 'relative' }}>
                        {isCreatingBranch && (
                            <div className="branch-modal-loading-overlay">
                                <div className="branch-modal-loading-content">
                                    <div className="branch-creating-spinner">
                                        <div className="branch-creating-spinner-ring"></div>
                                        <div className="branch-creating-spinner-ring"></div>
                                        <div className="branch-creating-spinner-ring"></div>
                                    </div>
                                    <p className="branch-modal-loading-text">Creating branch...</p>
                                    <p className="branch-modal-loading-subtext">This may take a few moments</p>
                                </div>
                            </div>
                        )}
                        <div className="modal-header">
                            <h3>{editingBranch ? 'Manage Branch' : 'Create New Branch'}</h3>
                            <button className="close-btn" onClick={handleCloseModal}>×</button>
                        </div>

                        <form onSubmit={handleCreateBranch}>
                            <div className="form-group">
                                <label>Branch Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="e.g. Downtown Campus"
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Login ID {editingBranch ? '(Cannot be changed)' : '(Unique)'}
                                        {!editingBranch && idStatus === 'checking' && <span className="status-text checking"> Checking...</span>}
                                        {!editingBranch && idStatus === 'available' && <span className="status-text available"> ✓ Available</span>}
                                        {!editingBranch && idStatus === 'taken' && <span className="status-text taken"> ✗ Taken</span>}
                                    </label>
                                    <input
                                        type="text"
                                        name="school_id"
                                        value={formData.school_id}
                                        onChange={handleInputChange}
                                        onBlur={!editingBranch ? handleIdBlur : undefined}
                                        required
                                        placeholder="e.g. SCHOOL-DT"
                                        readOnly={!!editingBranch}
                                        className={!editingBranch && idStatus === 'taken' ? 'input-error' : !editingBranch && idStatus === 'available' ? 'input-success' : ''}
                                        style={editingBranch ? { background: '#f3f4f6', cursor: 'not-allowed' } : {}}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Password</label>
                                    <input
                                        type="text"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Secret Password"
                                    />
                                </div>
                            </div>

                            <div className="quota-section">
                                <h4>License Allocation</h4>
                                <p className="quota-hint">Move sliders to assign capacity from your pool to this branch.</p>

                                <div className="form-group">
                                    <label>Student Capacity: {formData.max_students}</label>
                                    <input
                                        type="range"
                                        name="max_students"
                                        min="0"
                                        max={schoolData.max_students + (editingBranch ? editingBranch.max_students : 0)}
                                        value={formData.max_students}
                                        onChange={handleInputChange}
                                    />
                                    <div className="range-labels">
                                        <span>0</span>
                                        <span>Max: {schoolData.max_students + (editingBranch ? editingBranch.max_students : 0)}</span>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Teacher Capacity: {formData.max_teachers}</label>
                                    <input
                                        type="range"
                                        name="max_teachers"
                                        min="0"
                                        max={schoolData.max_teachers + (editingBranch ? editingBranch.max_teachers : 0)}
                                        value={formData.max_teachers}
                                        onChange={handleInputChange}
                                    />
                                    <div className="range-labels">
                                        <span>0</span>
                                        <span>Max: {schoolData.max_teachers + (editingBranch ? editingBranch.max_teachers : 0)}</span>
                                    </div>
                                </div>
                            </div>

                            {error && <div className="error-message">{error}</div>}
                            {success && <div className="success-message">{success}</div>}

                            <div className="modal-actions">
                                {editingBranch && (
                                    <button
                                        type="button"
                                        className="delete-btn-modal"
                                        onClick={handleDeleteBranchClick}
                                    >
                                        Delete
                                    </button>
                                )}
                                <button type="button" className="cancel-btn" onClick={handleCloseModal} disabled={isCreatingBranch}>Cancel</button>
                                <button type="submit" className="submit-btn" disabled={isCreatingBranch || loading}>
                                    {isCreatingBranch ? (
                                        <>
                                            <span className="branch-loading-spinner"></span>
                                            Creating Branch...
                                        </>
                                    ) : loading ? (
                                        <>
                                            <span className="branch-loading-spinner"></span>
                                            {editingBranch ? 'Updating...' : 'Creating...'}
                                        </>
                                    ) : (
                                        editingBranch ? 'Update Branch' : 'Create Branch'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Custom Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="branch-delete-modal-overlay" onClick={() => !loading && setShowDeleteConfirm(false)}>
                    <div className="branch-delete-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="branch-delete-modal-header">
                            <div className="branch-delete-modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3>Delete Branch?</h3>
                        </div>
                        <div className="branch-delete-modal-body">
                            {loading ? (
                                <div className="branch-delete-loading">Deleting...</div>
                            ) : (
                                <>
                                    <p>Are you sure you want to delete <b>{editingBranch?.name}</b>?</p>
                                    <p className="branch-delete-warning">
                                        This will permanently delete ALL students, teachers, and data associated with this branch. Licenses will be returned to your pool.
                                    </p>
                                </>
                            )}
                        </div>
                        {!loading && (
                            <div className="branch-delete-modal-actions">
                                <button
                                    className="cancel-btn"
                                    onClick={() => setShowDeleteConfirm(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="delete-btn-modal"
                                    onClick={confirmDeleteBranch}
                                    style={{ margin: 0 }}
                                >
                                    Delete Branch
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Branch Created Success Modal */}
            {showSuccessModal && createdBranchData && (
                <div className="branch-success-overlay" onClick={handleCloseSuccessModal}>
                    <div className="branch-success-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="branch-success-header">
                            <div className="branch-success-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                </svg>
                            </div>
                            <h2 className="branch-success-title">Branch Created Successfully!</h2>
                        </div>
                        <div className="branch-success-body">
                            <p className="branch-success-message">
                                Your branch <strong>{createdBranchData.name}</strong> has been created and is now ready to use.
                            </p>
                            <div className="branch-success-details">
                                <div className="branch-success-detail-item">
                                    <span className="branch-success-detail-label">Branch Name:</span>
                                    <span className="branch-success-detail-value">{createdBranchData.name}</span>
                                </div>
                                <div className="branch-success-detail-item">
                                    <span className="branch-success-detail-label">Login ID:</span>
                                    <span className="branch-success-detail-value">{createdBranchData.school_id}</span>
                                </div>
                                {createdBranchData.branch_name && (
                                    <div className="branch-success-detail-item">
                                        <span className="branch-success-detail-label">Location:</span>
                                        <span className="branch-success-detail-value">{createdBranchData.branch_name}</span>
                                    </div>
                                )}
                                <div className="branch-success-detail-item">
                                    <span className="branch-success-detail-label">Student Capacity:</span>
                                    <span className="branch-success-detail-value">{createdBranchData.max_students}</span>
                                </div>
                                <div className="branch-success-detail-item">
                                    <span className="branch-success-detail-label">Teacher Capacity:</span>
                                    <span className="branch-success-detail-value">{createdBranchData.max_teachers}</span>
                                </div>
                            </div>
                            <div className="branch-success-note">
                                <p>✓ Grades and subsections have been cloned from the parent school</p>
                                <p>✓ The branch is now visible in your branches list</p>
                            </div>
                        </div>
                        <div className="branch-success-actions">
                            <button className="branch-success-close-btn" onClick={handleCloseSuccessModal}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardSchoolBranches;
